import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'

import AppLayout from '../../components/app-layout/AppLayout'
import Button from '../../components/button/Button'
import { useAuth } from '../../_hooks/use-auth'
import { useMessagesUnread } from '../../_hooks/use-messages-unread'
import {
  getMessageThreads,
  getThreadMessages,
  markThreadRead,
  sendMessage,
} from '../../_data/messages'
import { QuickPassThreadMessage } from '../../_types/quickpass-message'
import { formatMessageTime } from './helpers'
import { MessagesPageState } from './types'
import {
  Composer,
  ComposerInput,
  Conversation,
  ConversationHeader,
  ConversationSubtitle,
  ConversationTitle,
  EmptyBody,
  EmptyState,
  EmptyTitle,
  Header,
  Layout,
  MessageBubble,
  MessageMeta,
  MessageRow,
  MessagesScroll,
  PageStack,
  PageSubtitle,
  PageTitle,
  ThreadItem,
  ThreadList,
  ThreadPreview,
  ThreadTime,
  ThreadTitle,
  ThreadUnread,
} from './styled'

const POLL_INTERVAL_MS = 6000

const initialiseState = (): MessagesPageState => ({
  threads: [],
  selectedEntityId: null,
  messages: [],
  isLoadingThreads: true,
  isLoadingMessages: false,
  isSending: false,
  draft: '',
})

/**
 * Renter inbox. Lists message threads (one per inquired property) and shows
 * the live conversation — the renter talks to the property's Casa AI agent
 * and its leasing team, who reply from the QuickCasa CRM. Polls the open
 * thread so PM replies appear without a refresh.
 *
 * @returns {JSX.Element}
 */
const MessagesPage = (): JSX.Element => {
  const { quickPassUser } = useAuth()
  const { refresh: refreshUnread } = useMessagesUnread()
  const [state, setState] = useState<MessagesPageState>(initialiseState)
  const scrollReference = useRef<HTMLDivElement | null>(null)

  const userId = quickPassUser?.id ?? ''

  const loadMessages = useCallback(
    async (entityId: string, withSpinner: boolean): Promise<void> => {
      if (userId === '') {
        return
      }
      if (withSpinner === true) {
        setState(previous => ({ ...previous, isLoadingMessages: true }))
      }
      const messages = await getThreadMessages(userId, entityId)
      setState(previous =>
        previous.selectedEntityId === entityId
          ? { ...previous, messages, isLoadingMessages: false }
          : previous,
      )
    },
    [userId],
  )

  useEffect(() => {
    if (userId === '') {
      return
    }
    let isMounted = true
    const load = async (): Promise<void> => {
      const threads = await getMessageThreads(userId)
      if (isMounted === false) {
        return
      }
      setState(previous => ({
        ...previous,
        threads,
        isLoadingThreads: false,
        selectedEntityId:
          previous.selectedEntityId ??
          (threads.length > 0 ? threads[0].entityId : null),
      }))
    }
    void load()
    return () => {
      isMounted = false
    }
  }, [userId])

  useEffect(() => {
    if (state.selectedEntityId === null || userId === '') {
      return
    }
    const entityId = state.selectedEntityId
    void loadMessages(entityId, true)

    const markRead = async (): Promise<void> => {
      await markThreadRead(userId, entityId)
      await refreshUnread()
    }
    void markRead()
    setState(previous => ({
      ...previous,
      threads: previous.threads.map(thread =>
        thread.entityId === entityId ? { ...thread, unreadCount: 0 } : thread,
      ),
    }))

    const handle = setInterval(() => {
      setState(previous => {
        if (previous.isSending === false) {
          void loadMessages(entityId, false)
        }
        return previous
      })
    }, POLL_INTERVAL_MS)

    return () => {
      clearInterval(handle)
    }
  }, [state.selectedEntityId, userId, loadMessages, refreshUnread])

  useEffect(() => {
    if (scrollReference.current !== null) {
      scrollReference.current.scrollTop = scrollReference.current.scrollHeight
    }
  }, [state.messages])

  const handleSelectThread = (entityId: string): void => {
    setState(previous => ({
      ...previous,
      selectedEntityId: entityId,
      messages: [],
    }))
  }

  const handleSend = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault()
      const entityId = state.selectedEntityId
      const text = state.draft.trim()
      if (entityId === null || text === '' || userId === '') {
        return
      }

      const optimistic: QuickPassThreadMessage = {
        id: `optimistic-${Date.now()}`,
        direction: 'out',
        message: text,
        fromName: '',
        fromPhoto: '',
        fromCasa: false,
        timestamp: Date.now(),
      }

      setState(previous => ({
        ...previous,
        draft: '',
        isSending: true,
        messages: [...previous.messages, optimistic],
      }))

      await sendMessage(userId, entityId, text)
      await loadMessages(entityId, false)
      setState(previous => ({ ...previous, isSending: false }))
    },
    [state.selectedEntityId, state.draft, userId, loadMessages],
  )

  const selectedThread =
    state.threads.find(thread => thread.entityId === state.selectedEntityId) ??
    null

  return (
    <AppLayout contentMaxWidth="standard">
      <PageStack>
        <Header>
          <PageTitle>Messages</PageTitle>
          <PageSubtitle>
            Chat with the leasing team for places you&apos;ve inquired about.
          </PageSubtitle>
        </Header>

        {state.isLoadingThreads === false && state.threads.length === 0 ? (
          <Conversation>
            <EmptyState>
              <EmptyTitle>No conversations yet</EmptyTitle>
              <EmptyBody>
                Inquire about a place on Discover and your conversation with the
                leasing team will show up here.
              </EmptyBody>
            </EmptyState>
          </Conversation>
        ) : (
          <Layout>
            <ThreadList>
              {state.threads.map(thread => (
                <ThreadItem
                  key={thread.entityId}
                  type="button"
                  isSelected={thread.entityId === state.selectedEntityId}
                  onClick={() => handleSelectThread(thread.entityId)}
                >
                  <ThreadTitle>{thread.title}</ThreadTitle>
                  <ThreadPreview>
                    {thread.lastMessage !== ''
                      ? thread.lastMessage
                      : 'Start the conversation'}
                  </ThreadPreview>
                  <ThreadTime>
                    {formatMessageTime(thread.lastMessageAt)}
                  </ThreadTime>
                  {thread.unreadCount > 0 ? (
                    <ThreadUnread>{thread.unreadCount}</ThreadUnread>
                  ) : null}
                </ThreadItem>
              ))}
            </ThreadList>

            <Conversation>
              {selectedThread !== null ? (
                <>
                  <ConversationHeader>
                    <ConversationTitle>
                      {selectedThread.title}
                    </ConversationTitle>
                    {selectedThread.address !== '' ? (
                      <ConversationSubtitle>
                        {selectedThread.address}
                      </ConversationSubtitle>
                    ) : null}
                  </ConversationHeader>

                  <MessagesScroll ref={scrollReference}>
                    {state.messages.length === 0 &&
                    state.isLoadingMessages === false ? (
                      <EmptyState>
                        <EmptyBody>
                          Say hello — ask about availability, a tour, or
                          anything about the place.
                        </EmptyBody>
                      </EmptyState>
                    ) : (
                      state.messages.map(message => (
                        <MessageRow
                          key={message.id}
                          direction={message.direction}
                        >
                          <MessageBubble direction={message.direction}>
                            {message.message}
                          </MessageBubble>
                          <MessageMeta>
                            {message.direction === 'in' &&
                            message.fromName !== ''
                              ? `${message.fromName} · `
                              : ''}
                            {formatMessageTime(message.timestamp)}
                          </MessageMeta>
                        </MessageRow>
                      ))
                    )}
                  </MessagesScroll>

                  <Composer
                    onSubmit={event => {
                      void handleSend(event)
                    }}
                  >
                    <ComposerInput
                      value={state.draft}
                      onChange={event =>
                        setState(previous => ({
                          ...previous,
                          draft: event.target.value,
                        }))
                      }
                      placeholder="Type a message…"
                      rows={1}
                    />
                    <Button
                      type="submit"
                      size="medium"
                      isLoading={state.isSending}
                    >
                      Send
                    </Button>
                  </Composer>
                </>
              ) : (
                <EmptyState>
                  <EmptyBody>Select a conversation to get started.</EmptyBody>
                </EmptyState>
              )}
            </Conversation>
          </Layout>
        )}
      </PageStack>
    </AppLayout>
  )
}

export default MessagesPage
